/**
 * Video Generation Service
 *
 * Integrates with backend VideoGeneration API for fashion animation and 360 views
 */

import { apiClient as api } from './api';

const VIDEO_BASE_URL = '/api/VideoGeneration';

export interface VideoGenerationRequest {
  model: string;
  prompt: string;
  duration?: number;
  generateAudio?: boolean;
  referenceImage?: string;
  firstFrameImage?: string;
  lastFrameImage?: string;
  seed?: number;
  width?: number;
  height?: number;
  fps?: number;
  sessionId?: string;   // For grouping assets by session
  userId?: string;      // For user-specific asset retrieval
  subType?: string;     // For categorizing video type (e.g., 'animation', '360')
}

export interface VideoJobStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  videoUrl?: string;
  thumbnailUrl?: string;
  audioUrl?: string;
  estimatedTimeRemaining?: number;
  error?: string;
}

export interface VideoFile {
  url: string;
  data: string | null;
  duration: number;
  resolution: string;
  contentType: string;
  hasAudio: boolean;
  metadata: any;
}

export interface VideoGenerationResponse {
  videos: VideoFile[];
  provider: string;
  model: string;
  requestId: string;
  timings: any;
  metadata: any;

  // Legacy fields for backward compatibility
  jobId?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  duration?: number;
  status?: string;
}

/**
 * Fashion Studio Video Models
 *
 * Updated to match UI Integration Guide recommendations
 * Best models for fashion use cases based on backend support
 */
export const FASHION_VIDEO_MODELS = {
  // Premium quality for final output
  VEO_31: 'veo-3.1',                        // $0.28/sec - Premium Google model (latest)
  VEO_31_FAST: 'veo-3.1-fast',              // $0.18/sec - Faster VEO variant
  KLING_PRO: 'kling-2.5-turbo-pro',         // $0.20/sec - Professional Kling

  // Balanced quality/speed
  KLING_STANDARD: 'kling-2.5-turbo',        // $0.12/sec - Standard Kling
  HAILUO_PRO: 'hailuo-02-pro',              // $0.15/sec - Professional Hailuo
  HAILUO_STANDARD: 'hailuo-02-standard',    // $0.08/sec - Reliable & Affordable
  WAN_25: 'wan-2.5',                        // $0.20/sec - Image-to-video specialist

  // Fast preview
  LTX_VIDEO: 'ltx-video'                    // $0.02/sec - Real-time preview
} as const;

export const videoGenerationService = {
  /**
   * Synchronous video generation (server-side polling)
   * Use for simple workflows where user can wait
   */
  async generateVideoSync(request: VideoGenerationRequest): Promise<VideoGenerationResponse> {
    try {
      const response = await api.post<VideoGenerationResponse>(`${VIDEO_BASE_URL}/generate`, {
        model: request.model,
        prompt: request.prompt,
        referenceImage: request.referenceImage,
        firstFrameImage: request.firstFrameImage,
        lastFrameImage: request.lastFrameImage,
        duration: request.duration || 5,
        generateAudio: request.generateAudio ?? false,
        seed: request.seed,
        width: request.width,
        height: request.height,
        fps: request.fps,
        sessionId: request.sessionId,  // Pass sessionId for asset grouping
        userId: request.userId,         // Pass userId for asset retrieval
        subType: request.subType        // Pass subType for categorization
      });

      // Parse the response and add backward compatible fields
      const result = response.data;

      // Extract first video URL for backward compatibility
      if (result.videos && result.videos.length > 0) {
        result.videoUrl = result.videos[0].url;
        result.duration = result.videos[0].duration;
      }

      return result;
    } catch (error: any) {
      console.error('Video generation failed:', error);
      throw new Error(error.response?.data?.error?.message || 'Failed to generate video');
    }
  },

  /**
   * Asynchronous video generation (client-side polling)
   * Use for production workflows with multiple concurrent jobs
   */
  async submitVideoJob(request: VideoGenerationRequest): Promise<VideoJobStatus> {
    try {
      const response = await api.post<VideoJobStatus>(`${VIDEO_BASE_URL}/jobs`, {
        model: request.model,
        prompt: request.prompt,
        referenceImage: request.referenceImage,
        firstFrameImage: request.firstFrameImage,
        lastFrameImage: request.lastFrameImage,
        duration: request.duration || 5,
        generateAudio: request.generateAudio ?? false,
        seed: request.seed,
        width: request.width,
        height: request.height,
        fps: request.fps
      });

      return response.data;
    } catch (error: any) {
      console.error('Failed to submit video job:', error);
      throw new Error(error.response?.data?.error?.message || 'Failed to submit video job');
    }
  },

  /**
   * Get video job status
   */
  async getJobStatus(jobId: string): Promise<VideoJobStatus> {
    try {
      const response = await api.get<VideoJobStatus>(`${VIDEO_BASE_URL}/jobs/${jobId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get job status:', error);
      throw new Error(error.response?.data?.error?.message || 'Failed to get job status');
    }
  },

  /**
   * Cancel a video generation job
   */
  async cancelJob(jobId: string): Promise<void> {
    try {
      await api.delete(`${VIDEO_BASE_URL}/jobs/${jobId}`);
    } catch (error: any) {
      console.error('Failed to cancel job:', error);
      throw new Error(error.response?.data?.error?.message || 'Failed to cancel job');
    }
  },

  /**
   * Animate a fashion design image
   * Best models: hailuo-02-standard (reliable), kling-2.5-turbo, wan-2.5 (I2V specialist)
   */
  async animateDesign(
    imageUrl: string,
    prompt: string,
    options: {
      model?: string;
      duration?: number;
      seed?: number;
      sessionId?: string;
      userId?: string;
      subType?: string;
    } = {}
  ): Promise<VideoGenerationResponse> {
    return this.generateVideoSync({
      model: options.model || FASHION_VIDEO_MODELS.HAILUO_STANDARD,
      prompt: prompt || 'Smooth fabric movement, gentle camera pan, professional fashion presentation',
      referenceImage: imageUrl,
      duration: options.duration || 5,
      generateAudio: false,
      seed: options.seed,
      width: 1024,
      height: 1024,
      sessionId: options.sessionId,
      userId: options.userId,
      subType: options.subType || 'animation'  // Default to 'animation'
    });
  },

  /**
   * Generate 360-degree turnaround view
   * Uses image-to-video with rotation prompt
   */
  async generate360View(
    imageUrl: string,
    options: {
      model?: string;
      duration?: number;
      seed?: number;
      sessionId?: string;
      userId?: string;
      subType?: string;
    } = {}
  ): Promise<VideoGenerationResponse> {
    const rotationPrompt = 'Smooth 360-degree rotation of the fashion design on a turntable, ' +
      'professional studio lighting, seamless loop, maintain all details, ' +
      'slow steady rotation showing all angles';

    return this.generateVideoSync({
      model: options.model || FASHION_VIDEO_MODELS.HAILUO_STANDARD,
      prompt: rotationPrompt,
      referenceImage: imageUrl,
      duration: options.duration || 8,  // Longer for full rotation
      generateAudio: false,
      seed: options.seed,
      width: 1024,
      height: 1024,
      sessionId: options.sessionId,
      userId: options.userId,
      subType: options.subType || '360'  // Default to '360'
    });
  },

  /**
   * Get available video providers
   */
  async getProviders(): Promise<any> {
    try {
      const response = await api.get(`${VIDEO_BASE_URL}/providers`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get providers:', error);
      throw new Error('Failed to get video providers');
    }
  }
};

export default videoGenerationService;
